import { FC, useState, useCallback, useContext } from "react";
import { useNavigate, useMatch, useLocation } from "react-router-dom";
import { Box, Tabs, Tab, LinearProgress, Typography } from "@mui/material";
import { TrainingFeedSideMenuContent } from "./TrainingFeedSideMenuContent";
import { makeStyles } from "tss-react/mui";
import { palette } from "@/constants";
import {
	useGetCourseDetails,
	useGetCourseCurriculum,
	type CurriculumModuleDto,
	type GetCourseCurriculumResponse,
} from "@/app2/shared/data";
import { useMq } from "@buildwitt/shapeshift-common/v1/hooks";
import { CourseCurriculumModule } from "@/app2/components/CourseCurriculumModule";
import CourseProgressionSummary from "./CourseProgressionSummary";
import { useSSTranslation } from "@/i18n/useSSTranslation";
import useDevCycleSdk from "@/hooks/useDevCycleSdk";
import { App2NotificationContext, App2NotificationSeverity } from "@/app2/shared/contexts/App2NotificationContext";
import { LocaleContext, resolveLocale } from "@/app2/shared/contexts";

/** First incomplete module in order (for linear progression). */
function getNextIncompleteModule(items: CurriculumModuleDto[]): CurriculumModuleDto | undefined {
	return items.find((item) => !item.complete);
}

// Layout and styles aligned with shapeshift-library v1 courseSideMenuContent
const useStyles = makeStyles<{ isNewHomeScreen: boolean }>()((theme, { isNewHomeScreen }) => ({
	sideMenu: {
		"[class*='moduleTitle']": {
			color: "white",
		},
		borderRadius: "16px",
		margin: "15px",
		padding: isNewHomeScreen ? 0 : "15px 25px",
		background: "#282828",
	},
	courseSideBar: {
		padding: "25px",
		overflowY: "auto",
	},
	courseSideBarModules: {
		marginTop: theme.spacing(5),
	},
	tabs: {
		height: "70px",
		minHeight: "70px",
		borderBottom: palette.grey10 + " 1px solid",
		width: "100%",
		"& .MuiTabs-flexContainer": {
			width: "100%",
		},
		"& button": {
			flex: 1,
			padding: "6px 8px",
		},
	},
	moduleTab: {
		fontSize: "17px",
		background: "transparent !important",
	},
	moduleItem: {
		margin: "10px 0 12px",
	},
}));

interface CourseSideMenuContentProps {
	courseSlug: string;
	setSideMenuOpen?: (open: boolean) => void;
}

/**
 * Course Side Menu Content
 *
 * Reimplements the behavior of shapeshift-common v1 courseSideMenuContent using
 * app2 data (useGetCourseDetails, useGetCourseCurriculum) and generated types.
 *
 * - Course progress summary + curriculum list
 * - Linear progression: blocks navigation to non-next incomplete module and notifies
 * - Tabs: Today's Learning (feed) | Course Content (curriculum)
 * - Default tab from route: /feed/ → Today's Learning, /course/ → Course Content
 */
export const CourseSideMenuContent: FC<CourseSideMenuContentProps> = ({
	courseSlug,
	setSideMenuOpen,
}) => {
	const { newHomeScreen } = useDevCycleSdk();
	const { classes, cx } = useStyles({ isNewHomeScreen: newHomeScreen });
	const { t } = useSSTranslation();
	const mq = useMq();
	const navigate = useNavigate();
	const location = useLocation();
	const { notify } = useContext(App2NotificationContext);

	const pageIsAnnouncement = useMatch("/feed/announcement/*");

	const defaultTab = location.pathname.startsWith("/feed/") ? "training-feed" : "course";
	const [tab, setTab] = useState<"training-feed" | "course">(defaultTab);

	const [{ locale }] = useContext(LocaleContext);
	const detailsParams = { locale: resolveLocale(locale) };
	const curriculumParams = { locale: resolveLocale(locale) };

	const { data: courseData } = useGetCourseDetails(courseSlug, detailsParams);
	const { data: curriculum, isLoading: isLoadingCurriculum } = useGetCourseCurriculum(courseSlug, curriculumParams, {
		enabled: !!courseData?.course?.enrollment,
	});

	const course = courseData?.course;

	const handleModuleClick = useCallback(
		(curriculumData: GetCourseCurriculumResponse, item: CurriculumModuleDto) => {
			if (item.isLocked) {
				const next = getNextIncompleteModule(curriculumData.items);
				const lessonTitle = next?.title ?? t("course_lesson_next_lesson", "the next lesson");
				notify(
					t("course_lesson_sequence_warning_message", "Lessons must be completed in sequence. Next up is {{lessonTitle}}.", { lessonTitle }),
					t("course_lesson_sequence_warning_title", "Hey now, slow down!"),
					App2NotificationSeverity.warning,
					5000
				);
				return;
			}

			navigate(`/course/${courseSlug}/${item.type}/${item.slug}`);

			if (mq.mobile) {
				setSideMenuOpen?.(false);
			}
		},
		[courseSlug, navigate, mq.mobile, notify, setSideMenuOpen, t]
	);

	if (!course) {
		return null;
	}

	return (
		<>
			<Tabs className={classes.tabs} value={tab} onChange={(_e, v) => setTab(v)}>
				<Tab
					className={classes.moduleTab}
					label={t("home_today_learning", "Today's Learning")}
					value="training-feed"
				/>
				{!pageIsAnnouncement && (
					<Tab className={classes.moduleTab} label={t("course_content_title", "Course Content")} value="course" />
				)}
			</Tabs>

			{tab === "course" && (
				<Box className={classes.sideMenu}>
					{isLoadingCurriculum && (
						<Box py={2}>
							<LinearProgress />
						</Box>
					)}

					{!isLoadingCurriculum && curriculum && (
						<Box className={classes.courseSideBar}>
							{course.enrollment && (
								<CourseProgressionSummary
									title={course.title}
									percentComplete={curriculum.progressPercent}
									completedModules={curriculum.completedItems}
									totalModules={curriculum.totalItems}
								/>
							)}

							{curriculum.items.length > 0 ? (
								<Box className={classes.courseSideBarModules}>
									{curriculum.items.map((item) => (
										<CourseCurriculumModule
											key={item.id}
											courseSlug={courseSlug}
											module={item}
											onClick={() => curriculum && handleModuleClick(curriculum, item)}
											className={cx(classes.moduleItem)}
										/>
									))}
								</Box>
							) : (
								<Box py={2}>
									<Typography variant="body2" color="text.secondary">
										No curriculum items available.
									</Typography>
								</Box>
							)}
						</Box>
					)}
				</Box>
			)}

			{tab === "training-feed" && <TrainingFeedSideMenuContent />}
		</>
	);
};
